import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useRef, useCallback, useEffect, useImperativeHandle } from 'react';
/**
 * Renders an <a href="#"> with a native `onclick` DOM handler.
 * jQuery UI Menu captures events on menu items, so we set the handler
 * via ref as a DOM property (not attribute) to avoid global function
 * collisions when multiple dropdowns are mounted.
 */
function MenuAction({ onAction, children }) {
    const ref = useRef(null);
    const handlerRef = useRef(onAction);
    handlerRef.current = onAction;
    useEffect(() => {
        const el = ref.current;
        if (!el)
            return;
        const handler = (e) => {
            e.preventDefault();
            handlerRef.current();
        };
        el.addEventListener('click', handler);
        return () => el.removeEventListener('click', handler);
    }, []);
    return _jsx("a", { href: "#", ref: ref, children: children });
}
const VersionsDropdown = React.forwardRef(function VersionsDropdown(props, ref) {
    const { fileId, currentVersionTitle, versions, linkId, onVersionChange, shouldShowVersionComparison, onVersionComparisonClick, fileTypeId, } = props;
    const containerRef = useRef(null);
    const menuRef = useRef(null);
    const compareButtonRef = useRef(null);
    useImperativeHandle(ref, () => ({
        getContainerElement: () => containerRef.current,
    }), []);
    const comparisonMenuRef = useRef(null);
    const showVersionComparisonDropdown = useCallback(() => {
        if (comparisonMenuRef.current) {
            try {
                $(comparisonMenuRef.current).menu('destroy');
            }
            catch (exception) {
                console.log(`Failed to destroy menu: ${exception}`);
            }
            comparisonMenuRef.current.remove();
        }
        const el = document.createElement('ul');
        el.className = 'menu';
        for (const version of versions) {
            if (!version.isCurrent) {
                const li = document.createElement('li');
                const a = document.createElement('a');
                a.href = '#';
                a.onclick = (e) => {
                    e.preventDefault();
                    onVersionComparisonClick?.(version.id);
                    // Cleanup
                    $(el).menu('destroy');
                    el.remove();
                    comparisonMenuRef.current = null;
                };
                a.textContent = version.title;
                li.appendChild(a);
                el.appendChild(li);
            }
        }
        // Attach to document first
        (containerRef.current ?? document.body).appendChild(el);
        $(el).show().menu();
        // Calculate position
        if (compareButtonRef.current) {
            const rect = compareButtonRef.current.getBoundingClientRect();
            el.style.left = (compareButtonRef.current.offsetLeft + (rect.width / 2) - (el.clientWidth / 2)) + 'px';
        }
        comparisonMenuRef.current = el;
    }, [versions, onVersionComparisonClick]);
    const closeVersionMenu = () => {
        if (menuRef.current) {
            try {
                $(menuRef.current).menu('destroy');
            }
            catch (exception) {
                console.log(`Failed to destroy menu: ${exception}`);
            }
        }
    };
    const closeComparisonMenu = () => {
        if (comparisonMenuRef.current) {
            try {
                $(comparisonMenuRef.current).menu('destroy');
            }
            catch (exception) {
                console.log(`Failed to destroy menu: ${exception}`);
            }
            comparisonMenuRef.current.remove();
            comparisonMenuRef.current = null;
        }
    };
    const handleButtonClick = useCallback(() => {
        closeComparisonMenu();
        if (menuRef.current) {
            $(menuRef.current).show().menu();
        }
    }, []);
    // Don't render if there's only one version (or none)
    if (versions.length <= 1)
        return null;
    // Build the click handler for a version menu item.
    // If onVersionChange is provided, call it directly (per-column);
    // otherwise fall back to loadFile() for dynamic file switching.
    const buildVersionAction = (targetFileId) => {
        if (onVersionChange) {
            return () => onVersionChange(targetFileId);
        }
        return () => {
            const w = window;
            if (typeof w.loadFile === 'function')
                w.loadFile(targetFileId);
        };
    };
    return (_jsxs("div", { ref: containerRef, children: [_jsxs("button", { onClick: handleButtonClick, className: "version-selector-button", "aria-label": "Select version", children: [currentVersionTitle, " \u25BE"] }), shouldShowVersionComparison && onVersionComparisonClick &&
                _jsxs("button", { ref: compareButtonRef, className: "version-add-compare-button tooltip hidden md:block compare-btn", "aria-label": "Compare version", title: "Compare version", onClick: showVersionComparisonDropdown, children: [_jsx("div", { className: "fa-icon fa-icon-plus-solid" }), " Compare"] }), _jsx("ul", { id: "revision_dropdown", ref: menuRef, className: "menu", style: { display: 'none', minWidth: '170px' }, children: versions.map(ver => (_jsx("li", { className: ver.isCurrent ? 'ui-checked' : '', children: ver.isCurrent ? (_jsxs("a", { href: "#", children: [_jsx("img", { className: `file-thumb small version-thumbnail${fileTypeId === 2 ? ' version-thumbnail--audio' : ''}`, src: ver.thumbUrl, alt: "" }), " ", ver.title] })) : (_jsxs(MenuAction, { onAction: buildVersionAction(ver.id), children: [_jsx("img", { className: `file-thumb small version-thumbnail${fileTypeId === 2 ? ' version-thumbnail--audio' : ''}`, src: ver.thumbUrl, alt: `${ver.title} thumbnail` }), " ", ver.title] })) }, ver.id))) })] }));
});
export default VersionsDropdown;
